import { doNotification } from "../../../components/toast/nofitication";
import { handleError } from "../../../helperFunctions";
import { BaseAPIResponse } from "../../../utils/types";
import { baseApi } from "../../api";
import {
  ContributionsApprovalPayload,
  ContributionsApprovalQuery,
  ContributionsApprovalsResponse,
  ContributionsBulkApprovalsResponse,
  CooperativeApprovalChainsResponse,
  CooperativeWithdrawalApprovalPayload,
  CooperativeWithdrawalApprovalQuery,
  CooperativeWithdrawalApprovalsResponse,
  DepositsApprovalPayload,
  DepositsApprovalQuery,
  DepositsApprovalsResponse,
  DepositsBulkApprovalsResponse,
  LoanRepaymentApprovalPayload,
  LoanRepaymentApprovalQuery,
  LoanRepaymentApprovalsResponse,
  LoanRequestApprovalPayload,
  LoanRequestApprovalQuery,
  LoanRequestApprovalsResponse,
  MembershipApprovalPayload,
  MembershipApprovalQuery,
  MembershipApprovalsResponse,
  MembershipBulkApprovalsResponse,
  MembershipExitApprovalPayload,
  MembershipExitApprovalQuery,
  MembershipExitApprovalsResponse,
  SharesApprovalPayload,
  SharesApprovalQuery,
  SharesApprovalsResponse,
  SharesBulkApprovalsResponse,
  UpdateCooperativeApprovalChainPayload,
} from "./approvals.type";

export const approvalsApi = baseApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    fetchCooperativeApprovalChains: builder.query<
      CooperativeApprovalChainsResponse,
      void
    >({
      query: () => {
        return {
          url: `/AccessMgmt/LevelApproval`,
          method: "GET",
          params: {
            group: true,
          },
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, false);
        }
      },
    }),

    removeCooperativeOfficial: builder.mutation<
      BaseAPIResponse,
      {
        official: number | string;
      }
    >({
      query: (payload) => {
        return {
          url: `/Cooperative/Register/Official/delete`,
          method: "POST",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    updateCooperativeApprovalChain: builder.mutation<
      BaseAPIResponse,
      UpdateCooperativeApprovalChainPayload
    >({
      query: (payload) => {
        return {
          url: `/AccessMgmt/LevelApproval`,
          method: "POST",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    fetchPendingContributionApprovals: builder.query<
      ContributionsApprovalsResponse | ContributionsBulkApprovalsResponse,
      ContributionsApprovalQuery
    >({
      query: (params) => {
        return {
          url: `/AccessMgmt/Wallet/Saving/Member/Approval`,
          method: "GET",
          params: params,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, false);
        }
      },
    }),

    approveContributions: builder.mutation<
      BaseAPIResponse,
      ContributionsApprovalPayload
    >({
      query: (payload) => {
        return {
          url: `/AccessMgmt/Wallet/Saving/Member/Approval`,
          method: "POST",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const resp = await queryFulfilled;
          if (resp.data.success && arg.logs.length > 0) {
            doNotification(
              arg.logs[0].answer
                ? "Approval Successful"
                : "Rejection Successful"
            );
          }
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    fetchPendingDepositApprovals: builder.query<
      DepositsApprovalsResponse | DepositsBulkApprovalsResponse,
      DepositsApprovalQuery
    >({
      query: (params) => {
        return {
          url: `/AccessMgmt/Wallet/Deposit/Member/Approval`,
          method: "GET",
          params: params,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, false);
        }
      },
    }),

    approveDeposits: builder.mutation<BaseAPIResponse, DepositsApprovalPayload>(
      {
        query: (payload) => {
          return {
            url: `/AccessMgmt/Wallet/Deposit/Member/Approval`,
            method: "POST",
            body: payload,
          };
        },
        async onQueryStarted(arg, { queryFulfilled }) {
          try {
            const resp = await queryFulfilled;
            if (resp.data.success && arg.logs.length > 0) {
              doNotification(
                arg.logs[0].answer
                  ? "Approval Successful"
                  : "Rejection Successful"
              );
            }
          } catch (err) {
            handleError(err, true);
          }
        },
      }
    ),

    fetchPendingSharesApprovals: builder.query<
      SharesApprovalsResponse | SharesBulkApprovalsResponse,
      SharesApprovalQuery
    >({
      query: (params) => {
        return {
          url: `/AccessMgmt/Wallet/Shares/Member/Approval`,
          method: "GET",
          params: params,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, false);
        }
      },
    }),

    approveShares: builder.mutation<BaseAPIResponse, SharesApprovalPayload>({
      query: (payload) => {
        return {
          url: `/AccessMgmt/Wallet/Shares/Member/Approval`,
          method: "POST",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const resp = await queryFulfilled;
          if (resp.data.success && arg.logs.length > 0) {
            doNotification(
              arg.logs[0].answer
                ? "Approval Successful"
                : "Rejection Successful"
            );
          }
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    fetchPendingMembershipApprovals: builder.query<
      MembershipApprovalsResponse | MembershipBulkApprovalsResponse,
      MembershipApprovalQuery
    >({
      query: (params) => {
        return {
          url: `/AccessMgmt/Member/Add/Approval`,
          method: "GET",
          params: params,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, false);
        }
      },
    }),

    approveMembership: builder.mutation<
      BaseAPIResponse,
      MembershipApprovalPayload
    >({
      query: (payload) => {
        return {
          url: `/AccessMgmt/Member/Add/Approval`,
          method: "POST",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const resp = await queryFulfilled;
          if (resp.data.success && arg.logs.length > 0) {
            doNotification(
              arg.logs[0].answer
                ? "Approval Successful"
                : "Rejection Successful"
            );
          }
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    fetchPendingLoanRequestApprovals: builder.query<
      LoanRequestApprovalsResponse,
      LoanRequestApprovalQuery
    >({
      query: (params) => {
        return {
          url: `/AccessMgmt/Member/Loan/Approval`,
          method: "GET",
          params: params,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, false);
        }
      },
    }),

    approveLoanRequest: builder.mutation<
      BaseAPIResponse,
      LoanRequestApprovalPayload
    >({
      query: (payload) => {
        return {
          url: `/AccessMgmt/Member/Loan/Approval`,
          method: "POST",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const resp = await queryFulfilled;
          if (resp.data.success && arg.logs.length > 0) {
            doNotification(
              arg.logs[0].answer
                ? "Approval Successful"
                : "Rejection Successful"
            );
          }
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    fetchCooperativeWithdrawalApprovals: builder.query<
      CooperativeWithdrawalApprovalsResponse,
      CooperativeWithdrawalApprovalQuery
    >({
      query: (params) => {
        return {
          url: `/AccessMgmt/Wallet/Withdrawal/member/Approval`,
          method: "GET",
          params: params,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, false);
        }
      },
    }),

    approveCooperativeWithdrawal: builder.mutation<
      BaseAPIResponse,
      CooperativeWithdrawalApprovalPayload
    >({
      query: (payload) => {
        return {
          url: `/AccessMgmt/Wallet/Withdrawal/member/Approval`,
          method: "POST",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const resp = await queryFulfilled;
          if (resp.data.success && arg.logs.length > 0) {
            doNotification(
              arg.logs[0].answer
                ? "Approval Successful"
                : "Rejection Successful"
            );
          }
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    fetchMembershipExitApprovals: builder.query<
      MembershipExitApprovalsResponse,
      MembershipExitApprovalQuery
    >({
      query: (params) => {
        return {
          url: `/AccessMgmt/Member/Removal/Approval`,
          method: "GET",
          params: params,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, false);
        }
      },
    }),

    approveMembershipExit: builder.mutation<
      BaseAPIResponse,
      MembershipExitApprovalPayload
    >({
      query: (payload) => {
        return {
          url: `/AccessMgmt/Member/Removal/Approval`,
          method: "POST",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const resp = await queryFulfilled;
          if (resp.data.success && arg.member.length > 0) {
            doNotification(
              arg.member[0].answer
                ? "Approval Successful"
                : "Rejection Successful"
            );
          }
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    fetchLoanRepaymentApprovals: builder.query<
      LoanRepaymentApprovalsResponse,
      LoanRepaymentApprovalQuery
    >({
      query: (params) => {
        return {
          url: `/AccessMgmt/Wallet/LoanRepayment/Member/Approval`,
          method: "GET",
          params: params,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, false);
        }
      },
    }),

    approveLoanRepayment: builder.mutation<
      BaseAPIResponse,
      LoanRepaymentApprovalPayload
    >({
      query: (payload) => {
        return {
          url: `/AccessMgmt/Wallet/LoanRepayment/Member/Approval`,
          method: "POST",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const resp = await queryFulfilled;
          if (resp.data.success && arg.logs.length > 0) {
            doNotification(
              arg.logs[0].answer
                ? "Approval Successful"
                : "Rejection Successful"
            );
          }
        } catch (err) {
          handleError(err, true);
        }
      },
    }),
  }),
});

export const {
  useFetchCooperativeApprovalChainsQuery,
  useRemoveCooperativeOfficialMutation,
  useUpdateCooperativeApprovalChainMutation,
  useFetchPendingContributionApprovalsQuery,
  useApproveContributionsMutation,
  useFetchPendingDepositApprovalsQuery,
  useApproveDepositsMutation,
  useFetchPendingSharesApprovalsQuery,
  useApproveSharesMutation,
  useFetchPendingMembershipApprovalsQuery,
  useApproveMembershipMutation,
  useFetchPendingLoanRequestApprovalsQuery,
  useApproveLoanRequestMutation,
  useFetchCooperativeWithdrawalApprovalsQuery,
  useApproveCooperativeWithdrawalMutation,
  useFetchMembershipExitApprovalsQuery,
  useApproveMembershipExitMutation,
  useFetchLoanRepaymentApprovalsQuery,
  useApproveLoanRepaymentMutation,
} = approvalsApi;
